import React, { useState } from 'react';
import { RiCloseLine, RiPlaneLine, RiCalendar2Line, RiTeamLine, RiMapPinLine, RiErrorWarningLine, RiRefreshLine, RiBuildingLine, RiPriceTag3Line, RiCheckLine, RiTrophyLine, RiMedalLine, RiLoader4Line, RiDownload2Line, RiFileLine, RiTimeLine, RiFlightTakeoffLine, RiFlightLandLine, RiArrowRightLine, RiCalendarLine, RiCalendarScheduleLine, RiFileCopyLine, RiPencilLine } from '@remixicon/react';
import {
  Drawer,
  DrawerContent,
  DrawerClose,
  DrawerHeader,
  DrawerTitle
} from '@components/ui/Drawer';
import { Skeleton } from '@components/ui/Skeleton';
import Modal from '@components/ui/Modal';
import { TrustScore } from '../../trust-score';
import { apiClient } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import ImageCarousel from '@components/ui/ImageCarousel';
import { buildFlightEmailHtml } from '@/utils/emailTemplates';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAppStore } from '@store/appStore';
import { formatPrice, trunc2 } from '@/utils';
import { getAircraftModelName, getOperatorWithLocation, getAircraftPhotos } from '@/features/tails/utils';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { formatUTCToDateTime } from '@/utils';
import { getQuoteStatusConfig } from '../utils';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, ACTIONS, ROLES, normalizeRole } from '@/lib/permissions';
import { useQuoteDuplicator } from '../hooks/useQuoteDuplicator';
import { resolveAssetUrl } from '@/utils/urls';

/**
 * QuoteDetailsDrawer Component
 * Displays detailed quote information in a drawer
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the drawer is open
 * @param {Function} props.onOpenChange - Callback when drawer open state changes
 * @param {string} props.quoteId - ID of the quote to display
 */
const QuoteDetailsDrawer = ({ isOpen, onOpenChange, quoteId }) => {

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSelectedBidOptions } = useAppStore();

  // "Build Trip Sheet" requires full CRUD on Booking & Quoting
  // (admin + broker only — junior_broker is draft-only, read-only roles excluded).
  const { can, role } = usePermissions();
  // flight_coordinator, finance, and compliance_qa shouldn't see Duplicate /
  // Edit actions on a quote — they're observers, not editors.
  const normalizedRole = normalizeRole(role);
  const isQuoteViewerRole =
    normalizedRole === ROLES.FLIGHT_COORDINATOR ||
    normalizedRole === ROLES.FINANCE ||
    normalizedRole === ROLES.COMPLIANCE_QA;
  const isFinanceRole = normalizedRole === ROLES.FINANCE;
  const canBuildTripSheet =
    can(MODULES.BOOKING, ACTIONS.CREATE) &&
    can(MODULES.BOOKING, ACTIONS.UPDATE) &&
    can(MODULES.BOOKING, ACTIONS.DELETE);

  const [isSending, setIsSending] = useState(false);
  const [flightActionLoading, setFlightActionLoading] = useState(null);
  const [showFlightPicker, setShowFlightPicker] = useState(null);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  // Themed-modal state for the duplicate flow (replaces window.confirm).
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);
  const [editPromptOpen, setEditPromptOpen] = useState(false);
  // Holds the newly-created duplicate so the "edit now?" dialog can navigate to it.
  const [duplicatedQuote, setDuplicatedQuote] = useState(null);

  const { duplicateQuote, isDuplicating, currentStep: duplicateStep } = useQuoteDuplicator();

  // Fetch quote details using React Query
  const { data: quote, isLoading: loading, error, refetch: loadQuoteDetails } = useQuery({
    queryKey: ['quoteDetails', quoteId],
    queryFn: async () => {
      console.log('QuoteDetailsDrawer: Fetching quote:', quoteId);
      const data = await apiClient.getQuoteById(quoteId);
      console.log('QuoteDetailsDrawer: Got quote data:', data);
      return data;
    },
    enabled: isOpen && !!quoteId,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Refetch every time drawer opens
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: 1000, // Wait 1 second between retries
  });

  const { data: organization } = useQuery({
    queryKey: ['organization', quote?.broker_org_id],
    queryFn: () => apiClient.getOrganizationById(quote.broker_org_id),
    enabled: !!quote?.broker_org_id,
  });

  const { data: flightData } = useQuery({
    queryKey: ['flightByQuote', quoteId],
    queryFn: () => apiClient.getFlights({ quote_id: quoteId, include: 'aircrafts' }),
    enabled: isOpen && !!quoteId,
  });

  const handleFlightStatusUpdate = async (status, flightId) => {
    const flights = Array.isArray(flightData) ? flightData : [];
    if (!flightId && flights.length > 1) {
      setShowFlightPicker(status.toLowerCase());
      return;
    }
    const targetId = flightId || flights[0]?.flight_id;
    if (!targetId) return;
    const loadingKey = `${status.toLowerCase()}-${targetId}`;
    setFlightActionLoading(loadingKey);
    try {
      await apiClient.patchFlight(targetId, { flight_status: status });
      queryClient.invalidateQueries(['quoteDetails', quoteId]);
      queryClient.invalidateQueries(['flightByQuote', quoteId]);
      
      // Generate tracking URLs for each flight
      const tokenResponse = await apiClient.createTrackingToken(quoteId, quote.bid_items?.[0]?.bid?.leg_itinerary?.length || '1');

      const trackingUrl = {
        flightId: targetId,
        route: `${flights[0]?.departure_airport || '—'} → ${flights[0]?.arrival_airport || '—'}`,
        url: `${window.location.origin}/track/${targetId}?token=${tokenResponse.tracking_token}`,
      };

      // Send flight tracking email to client
      if (quote?.client?.email && trackingUrl) {
        const emailHtml = buildFlightEmailHtml({
          quote,
          trackingUrl: trackingUrl,
          flights,
          organization,
        });

        const emailPayload = {
          From: "tools@trustjet.ai",
          To: quote.client.email,
          Subject: `Your Flight is Ready – Track Your ${quote.departure_airport} to ${quote.arrival_airport} Trip`,
          HtmlBody: emailHtml,
        };

        apiClient.sendMessage(emailPayload)
          .then(() => console.log('Flight tracking email sent to', quote.client.email))
          .catch((err) => console.error('Failed to send flight tracking email:', err));
      }
      
      
    } catch (e) {
      console.error(`Failed to set ${status}:`, e);
    } finally {
      setFlightActionLoading(null);
    }
  };

  // Clicking the Duplicate icon opens the themed confirm modal.
  const handleDuplicateQuote = () => {
    if (!quote || isDuplicating) return;
    setDuplicateConfirmOpen(true);
  };

  // Runs the duplication after the user confirms in the themed modal.
  // The modal stays open until the API work finishes so the broker sees the
  // spinner + step label on the Duplicate button — closing it pre-emptively
  // would make the action look instantaneous (or worse, ignored).
  const runDuplicate = async () => {
    if (!quote || isDuplicating) return;

    const result = await duplicateQuote(quote);
    setDuplicateConfirmOpen(false);
    if (result.success && result.data?.quote_id) {
      toast.success(`Duplicate created — ${result.data.quote_number || 'new draft'}`);
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      // Stash the duplicate so the "edit now?" modal can navigate to it.
      setDuplicatedQuote(result.data);
      setEditPromptOpen(true);
    }
  };

  // "Edit now?" modal — confirm jumps to /quotes/generate?quoteid=…
  const acceptEditDuplicate = () => {
    const id = duplicatedQuote?.quote_id;
    setEditPromptOpen(false);
    onOpenChange?.(false);
    if (id) navigate(`/quotes/generate?quoteid=${id}`);
  };

  const dismissEditDuplicate = () => {
    setEditPromptOpen(false);
    setDuplicatedQuote(null);
    onOpenChange?.(false);
  };

  const handleMarkAsPaid = async () => {
    if (isMarkingPaid || !quoteId) return;
    setIsMarkingPaid(true);
    try {
      await apiClient.updateQuote(quoteId, { status: 'ACCEPTED' });
      queryClient.invalidateQueries({ queryKey: ['quoteDetails', quoteId] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success('Quote marked as Paid');
    } catch (e) {
      console.error('Failed to mark quote as paid:', e);
      toast.error('Failed to mark quote as Paid');
    } finally {
      setIsMarkingPaid(false);
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent side="right" className="w-full max-w-md lg:max-w-2xl flex flex-col p-0" hideCloseButton>
        <DrawerHeader className="sr-only">
          <DrawerTitle>Quote Details</DrawerTitle>
        </DrawerHeader>

        {/* Header */}
        <div className="flex items-start justify-between flex-shrink-0 gap-4 pb-0 p-6">
          <div className="flex-1">
            {loading ? (
              <>
                <Skeleton className="h-8 w-48 mb-2" />
                <Skeleton className="h-6 w-64" />
              </>
            ) : (
              <>
                <h2 className="font-semibold text-gray-900 text-2xl pr-10">
                  Trip ({quote?.is_multi_leg && quote?.bid_items?.[0]?.bid?.leg_itinerary?.length > 1
                    ? (quote.bid_items[0].bid.leg_itinerary || []).map((leg, i, arr) => (
                        <span key={i} className="inline-flex items-center gap-1 whitespace-nowrap">
                          {leg.departure_airport}
                          <RiArrowRightLine className="w-4 h-4 text-gray-400 inline" />
                          {leg.arrival_airport}
                          {i < arr.length - 1 && <span className="text-gray-400">,&nbsp;</span>}
                        </span>
                      ))
                    : <span className="inline-flex items-center gap-1 whitespace-nowrap">{quote?.departure_airport} <RiArrowRightLine className="w-4 h-4 text-gray-400 inline" /> {quote?.arrival_airport}{quote?.is_round_trip && <><RiArrowRightLine className="w-4 h-4 text-gray-400 inline" /> {quote?.departure_airport}</>}</span>
                  })
                </h2>
                {quote?.client && 
                  <p className="text-lg text-gray-500 mt-0.5">
                  Quote for {quote.client.first_name} {quote.client.last_name}
                  </p>
                }
              </>
            )}
          </div>
          {!loading && (
            <div className="shrink-0 flex flex-col items-end gap-2 mt-12 mr-[-10px]">
              {(() => {
                const config = getQuoteStatusConfig(quote?.status);
                const Icon = config.icon;
                return (
                  <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md ${config.className}`}>
                    {Icon && <Icon className="w-3.5 h-3.5" />}
                    {config.label}
                  </span>
                );
              })()}
            </div>
          )}
        </div>

        {/* Action buttons row — anchored top-right, matches the X close button styling.
            Order: Duplicate | Edit | Close. */}
        <div className="absolute right-6 top-5 flex items-center gap-2">
          {quote && !isQuoteViewerRole && (
            <button
              type="button"
              onClick={handleDuplicateQuote}
              disabled={isDuplicating}
              title={isDuplicating ? (duplicateStep || 'Duplicating…') : 'Duplicate this quote into a new DRAFT'}
              className="p-1 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDuplicating ? (
                <RiLoader4Line className="h-4 w-4 animate-spin" />
              ) : (
                <RiFileCopyLine className="h-4 w-4" />
              )}
              <span className="sr-only">Duplicate</span>
            </button>
          )}
          {(() => {
            // Edit is only meaningful for pre-acceptance quotes. Once a quote
            // is ACCEPTED or in the flight lifecycle, the data is locked from
            // this entry point (pricing/legs may have downstream effects).
            const EDITABLE_STATUSES = new Set(['DRAFT', 'SENT', 'VIEWED', 'SIGNED']);
            const canEdit = quote && !isQuoteViewerRole && EDITABLE_STATUSES.has((quote.status || '').toUpperCase());
            if (!canEdit) return null;
            return (
              <button
                type="button"
                onClick={() => navigate(`/quotes/generate?quoteid=${quote.quote_id}`)}
                title="Edit this quote"
                className="p-1 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors focus:outline-none"
              >
                <RiPencilLine className="h-4 w-4" />
                <span className="sr-only">Edit</span>
              </button>
            );
          })()}
          <DrawerClose className="p-1 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors focus:outline-none">
            <RiCloseLine className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DrawerClose>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto pb-5 pt-0 p-6">
          {loading ? (
            <div className="pt-4 space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : error ? (
            <div className="m-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <RiErrorWarningLine className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-medium text-red-900 mb-2">Failed to load quote</h4>
                  <p className="text-sm text-red-700">{error?.message || 'Failed to load quote details'}</p>
                  <button
                    onClick={loadQuoteDetails}
                    className="mt-3 pr-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    <RiRefreshLine className="w-4 h-4" />
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          ) : quote ? (() => {
            const statusUpper = (quote.status || '').toUpperCase();
            const needsPaidAction =
              (statusUpper === 'SENT' || statusUpper === 'VIEWED' || statusUpper === 'SIGNED') &&
              (quote.payment_status || '').toUpperCase() === 'CAPTURED';
            return (
            <>
              {/* Already-paid notice — only when quote is pre-acceptance but payment captured */}
              {needsPaidAction && (
                <div className="pr-4 pt-4">
                  <div className="border border-red-200 bg-red-50 rounded-xl p-4">
                    <p className="text-sm text-red-700 mb-3">
                      This quote is already paid. Want to set the status to Paid?
                    </p>
                    <button
                      onClick={handleMarkAsPaid}
                      disabled={isMarkingPaid}
                      className="px-4 py-2 bg-primary-700 text-white text-sm font-medium rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                      {isMarkingPaid && <RiLoader4Line className="w-4 h-4 animate-spin" />}
                      {isMarkingPaid ? 'Marking...' : 'Mark as Paid'}
                    </button>
                  </div>
                </div>
              )}

              {/* Trip Details Section */}
              <div className="pt-4 pb-6">
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="p-4">
                    <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">TRIP DETAILS</h3>

                    {/* Row 1 — Passenger Count + Trip Type (first leg pair) */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {/* Passenger Count */}
                      <div className="flex items-start gap-2.5">
                        <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                          <RiTeamLine className="h-4 w-4 text-gray-600"/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-600 mb-1">Passengers</p>
                          <p className="text-base font-semibold text-gray-900">
                            {quote.passenger_count || 0}
                          </p>
                        </div>
                      </div>

                      {/* Trip Type — for multi-leg, span both columns on mobile only so the airport list has room */}
                      <div className={`flex items-start gap-2.5${quote.is_multi_leg && (quote.bid_items?.[0]?.bid?.leg_itinerary?.length || 0) > 1 ? ' col-span-2 md:col-span-1' : ''}`}>
                        <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                          <RiMapPinLine className="h-4 w-4 text-gray-600"/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-600 mb-1">
                            {quote.is_round_trip ? 'Round Trip' : quote.is_multi_leg ? 'Multi-Leg' : 'One-Way'}
                          </p>
                          <p className="text-base font-semibold text-gray-900">
                            {quote.is_multi_leg && (quote.bid_items?.[0]?.bid?.leg_itinerary?.length || 0) > 1
                              ? quote.bid_items[0].bid.leg_itinerary.map((leg, i, arr) => (
                                  <span key={i} className="inline-block whitespace-nowrap">
                                    {leg.departure_airport} → {leg.arrival_airport}{i < arr.length - 1 ? ', ' : ''}
                                  </span>
                                ))
                              : <>{quote.bid_items?.[0]?.bid?.leg_itinerary?.[0]?.departure_airport} → {quote.bid_items?.[0]?.bid?.leg_itinerary?.[0]?.arrival_airport}</>
                            }
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Row 2 — Departure Date (full width, first leg's date) */}
                    <div className="flex items-start gap-2.5 mb-4">
                      <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                        <RiCalendar2Line className="h-4 w-4 text-gray-600"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-600 mb-1">Departure Date</p>
                        <p className="text-base font-semibold text-gray-900">
                          {formatUTCToDateTime(
                            quote.bid_items?.[0]?.bid?.leg_itinerary?.[0]?.departure_date,
                            getAirportTimezone(quote.bid_items?.[0]?.bid?.leg_itinerary?.[0]?.departure_airport),
                            "dddd, MMMM D, YYYY"
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Per-leg compact rows, separated by dividers */}
                    <div className="space-y-2 border-t border-gray-100 pt-2">
                      {(quote.bid_items?.[0]?.bid?.leg_itinerary || []).map((leg, i) => (
                        <div key={`ml-${i}`} className="flex items-center justify-between border-b border-gray-100 pb-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                            <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightTakeoffLine className="w-4 h-4" /> {leg.departure_airport}</span>
                            <RiArrowRightLine className="w-4 h-4 text-gray-400" />
                            <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightLandLine className="w-4 h-4" />{leg.arrival_airport}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <RiCalendarLine className="w-3.5 h-3.5" />
                              <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), "MMM D")}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <RiTimeLine className="w-3.5 h-3.5" />
                              <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), "HH:mm")}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Expires pill */}
                  {quote.bid_items?.[0]?.bid?.valid_until && (
                  <div className="bg-gray-100 px-4 py-1.5 flex items-center justify-center gap-1.5 mx-4 mb-4 rounded-2xl">
                    <RiCalendarScheduleLine className="w-3.5 h-3.5 text-gray-500" />
                    <p className="text-xs text-gray-600">
                      Expires on {new Date(quote.bid_items?.[0]?.bid?.valid_until).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>)}
                </div>
              </div>

              {/* Aircraft Options Section */}
              {quote.bid_items && quote.bid_items.length > 0 && (
                <div className="pb-6">
                  {quote.bid_items.map((bidItem, bidIndex) => (
                    bidItem.bid?.bid_options?.filter(option => option.shortlisted === true).map((option, optionIndex) => (
                      <div key={`${bidIndex}-${optionIndex}`} className="mb-4 border border-gray-200 rounded-2xl overflow-hidden">

                        <div className="rounded-t-2xl overflow-hidden">
                          <ImageCarousel
                            images={(() => {
                              const photos = getAircraftPhotos(option.aircraft, true);
                              return Array.isArray(photos) ? photos : [photos];
                            })()}
                            alt={getAircraftModelName(option.aircraft)}
                            className="h-[350px]"
                          />
                        </div>

                        {/* Aircraft Details */}
                        <div className="p-4">
                          <div className="relative mb-2">
                            <div className="space-y-2">
                              <h4 className="text-xl font-semibold text-gray-900">{getAircraftModelName(option.aircraft)}</h4>
                              <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                <RiPlaneLine className="h-4 w-4"/>
                                 {option.aircraft?.tail_number}
                              </p>
                                {/* Operator */}
                                {option.operator?.name && (
                                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                                    <RiBuildingLine className="h-4 w-4"/>
                                    <span>{getOperatorWithLocation(option.operator)}</span>
                                    </div>
                                )}
                            </div>
                            {!isFinanceRole && (quote?.status != 'SENT' && quote?.status != 'ACCEPTED' && quote?.status != 'IN_TRANSIT') && (option.operator?.trust_score !== null && option.operator?.trust_score !== undefined) && (
                               <div className="absolute top-0 right-0 scale-75 origin-top-right">
                              <TrustScore score={option.operator.rating_tiers} numericScore={option.operator?.trust_score || 0} showBadge/>
                              </div>
                            )}
                          </div>

                          {/* Price */}
                          <div className="inline-flex items-center px-3 py-1.5 rounded-2xl gap-2 mb-3 bg-accent">
                            <RiPriceTag3Line className="h-4 w-4 font-bold text-[#0C3E71]" />
                            <span className="text-sm font-semibold text-[#0C3E71]">{formatPrice(option.total_price || option.final_price)}</span>
                          </div>

                          <div className="flex flex-wrap gap-2 mb-4">
                            {option.operator?.argus_rating && option.operator?.argus_rating!="No" &&
                            <span className={`flex px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-2xl font-medium`}>
                              <RiTrophyLine className="h-4 w-4 text-gray-600"/> {option.operator?.argus_rating}
                            </span>}
                            
                            {option.operator?.wyvern_rating && option.operator?.wyvern_rating!="No" &&
                            <span className={`flex px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-2xl font-medium`}>
                              <RiMedalLine className="h-4 w-4 text-gray-600"/> WINGMAN
                            </span>
                            }

                            {option.aircraft.aircraft_features?.no_aog &&
                            <span className={`flex px-2 py-1 bg-green-100 text-green-700 text-xs rounded-2xl font-medium`}>
                              <RiCheckLine className="h-4 w-4 text-gray-600"/> NO AOG
                            </span>
                            }
                          </div>

                          {/* About Aircraft
                          <div>
                            <h6 className="text-sm text-gray-500 font-[500]">About the Aircraft</h6>
                            <p className="text-sm text-gray-500">{option.aircraft?.description || "This aircraft offers exceptional comfort and performance for your journey. With a range of 3,600 nautical miles and seating for up to 10 passengers, it's perfect for your flight."}</p>
                          </div> */}

                          
                           <div className="rounded-xl mt-3">
                              <h6 className="text-sm text-gray-500 font-[500] mb-2">Amenities</h6>

                              {option.aircraft.aircraft_features?.custom_features && option.aircraft.aircraft_features?.custom_features?.length > 0 ? (
                                  <div className="grid grid-cols-1 gap-1">
                                    {option.aircraft.aircraft_features?.custom_features.map((feature, index) => (
                                      <div key={index} className="flex border-b py-1">
                                        <div className="w-10 h-7 flex items-center justify-center flex-shrink-0">
                                          <RiCheckLine className="w-5 h-5 text-gray-500" />
                                        </div>
                                        <div>
                                          <div className="text-sm text-gray-500">{feature}</div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                              ): (
                                <p className="text-sm text-gray-400">No amenities included.</p>
                              )}
                            </div> 


                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mt-3">

                              {quote?.files?.tripsheet && (
                                <button
                                  className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 text-xs rounded-full hover:bg-gray-50 transition-colors whitespace-nowrap"
                                  onClick={() => {
                                    window.open(resolveAssetUrl(quote.files.tripsheet), '_blank');
                                  }}
                                >
                                  <RiDownload2Line className="w-3.5 h-3.5" />
                                  <span>Download PDF</span>
                                </button>
                              )}

                              <button
                                className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 text-xs rounded-full hover:bg-gray-50 transition-colors whitespace-nowrap"
                                onClick={() => {
                                  const contractUrl = quote?.files?.contract || organization?.branding?.contract;
                                  if (contractUrl) {
                                    window.open(resolveAssetUrl(contractUrl), '_blank');
                                  }
                                }}
                              >
                                <RiFileLine className="w-3.5 h-3.5" />
                                <span>Review Contract</span>
                              </button>

                              {canBuildTripSheet && (!quote?.status || (quote?.status !== 'ACCEPTED' && quote?.status !== 'IN_TRANSIT' && quote?.status !== 'PASSENGERS_PENDING')) &&
                              <button
                                className="col-span-2 lg:col-span-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-accent text-primary-500 text-xs rounded-full transition-colors whitespace-nowrap"
                                onClick={() => {
                                  setSelectedBidOptions([option]);
                                  navigate(`/checkout?quoteid=${quote.quote_id}&option=${option.bid_option_id}`);
                                }}
                                disabled={isSending}
                              >
                                <RiPlaneLine className="w-3.5 h-3.5" />
                                <span>Book This Flight</span>
                              </button>
                              }

                            </div>
                       
                        </div>
                      </div>
                    ))
                  ))}
                </div>
              )}

              {/* Cancel & Set Positioning - only if flight exists for this quote */}
              {/* {hasFlight && (quote?.status?.toUpperCase() === 'ACCEPTED' || quote?.status?.toUpperCase() === 'IN_TRANSIT') && (
                <div className="flex gap-3 px-3">
                  <button
                    disabled={!!flightActionLoading}
                    className="flex-1 flex items-center justify-center gap-2 pr-4 py-2.5 bg-red-50 text-red-600 text-sm rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleFlightStatusUpdate('CANCELLED')}
                  >
                    {flightActionLoading === 'cancel' ? <RiLoader4Line className="w-4 h-4 animate-spin" /> : <RiCloseLine className="w-4 h-4" />}
                    <span>Cancel</span>
                  </button>
                  {Array.isArray(flightData) && flightData.length > 0 && flightData.every(f => f.flight_status?.toUpperCase() === 'POSITIONING') ? (
                    <span className="flex-1 flex items-center justify-center gap-2 pr-4 py-2.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-xl">
                      <RiMapPinLine className="w-4 h-4" />
                      <span>Positioning</span>
                    </span>
                  ) : (
                    <button
                      disabled={!!flightActionLoading}
                      className="flex-1 flex items-center justify-center gap-2 pr-4 py-2.5 bg-primary-500 text-white text-sm rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleFlightStatusUpdate('POSITIONING')}
                    >
                      {flightActionLoading === 'positioning' ? <RiLoader4Line className="w-4 h-4 animate-spin" /> : <RiMapPinLine className="w-4 h-4" />}
                      <span>Set Positioning</span>
                    </button>
                  )}
                </div>
              )} */}

              {/* Flight Picker Popup */}
              {canBuildTripSheet && showFlightPicker && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                  <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
                    <div className="flex items-center justify-between pr-4 py-3 border-b">
                      <h3 className="text-sm font-semibold text-gray-900">Select a flight for <b>{flightData[0]?.aircraft?.tail_number || flightData[0]?.tail_number || 'N/A'}</b></h3>
                      <button onClick={() => setShowFlightPicker(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                        <RiCloseLine className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto divide-y">
                      {(Array.isArray(flightData) ? flightData : []).map((flight) => (
                        <div key={flight.flight_id} className="flex items-center justify-between pr-4 py-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-500 truncate">
                              <b>{flight.departure_airport || '—'}</b> → <b>{flight.arrival_airport || '—'}</b>
                            </p>
                          </div>
                          {flight.flight_status?.toUpperCase() === 'POSITIONING' ? (
                            <span className="ml-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded-lg">
                              <RiMapPinLine className="w-3.5 h-3.5" />
                              Positioning
                            </span>
                          ) : (
                            <button
                              disabled={flightActionLoading === `positioning-${flight.flight_id}`}
                              className="ml-3 flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 text-white text-xs rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleFlightStatusUpdate('POSITIONING', flight.flight_id)}
                            >
                              {flightActionLoading === `positioning-${flight.flight_id}` ? (
                                <RiLoader4Line className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <RiMapPinLine className="w-3.5 h-3.5" />
                              )}
                              <span>Set Positioning</span>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {quote.bid_items?.[0]?.bid?.valid_until && (
                <div className="d-block pr-4">
                  <p className="text-sm bg-gray-100 text-gray-900 px-5 py-4 rounded-full">
                    This quote is valid until {formatUTCToDateTime(quote.bid_items?.[0]?.bid?.valid_until, 'UTC', 'dddd, MMMM D, YYYY')}. Prices are subject to availability and may change.
                  </p>
                </div>
              )}
            </>
            );
          })() : null}
        </div>

        {/* Floating footer — only shown when the quote is at a stage where a trip sheet makes sense.
            The mobile bottom nav (z-index 1000, fixed bottom) overlays the drawer (z-50), so we lift
            the footer above it on mobile. The nav is hidden at md+, so no spacer needed there. */}
        {canBuildTripSheet && quote && (quote.status === 'ACCEPTED' || quote.status === 'PASSENGERS_PENDING' || quote.status === 'WAITING_FLIGHT') && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3 mb-[72px] md:mb-0">
            <button
              type="button"
            onClick={() => navigate(`/trip-sheet?quoteid=${quote.quote_id}`)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-700 hover:bg-primary-800 text-white text-sm font-medium rounded-full transition-colors"
            >
              <RiFileLine className="w-4 h-4" />
              <span>Build Trip Sheet</span>
            </button>
          </div>
        )}
      </DrawerContent>

      {/* Themed dialogs replacing window.confirm for the duplicate flow */}
      <Modal
        isOpen={duplicateConfirmOpen}
        onClose={() => !isDuplicating && setDuplicateConfirmOpen(false)}
        title="Duplicate this quote?"
        size="sm"
        closeOnOutsideClick={!isDuplicating}
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
              {isDuplicating
                ? <RiLoader4Line className="w-5 h-5 text-primary-600 animate-spin" />
                : <RiFileCopyLine className="w-5 h-5 text-primary-600" />}
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-700 leading-relaxed">
                A new <span className="font-semibold">DRAFT</span> will be created with the same
                trip details — pricing and passengers carry over so you can edit before sending.
              </p>
              {isDuplicating && (
                <p className="mt-2 text-xs text-primary-700 font-medium" aria-live="polite">
                  {duplicateStep || 'Duplicating…'}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setDuplicateConfirmOpen(false)}
              disabled={isDuplicating}
              className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={runDuplicate}
              disabled={isDuplicating}
              className="px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {isDuplicating && <RiLoader4Line className="w-4 h-4 animate-spin" />}
              {isDuplicating ? (duplicateStep || 'Duplicating…') : 'Duplicate'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={editPromptOpen}
        onClose={dismissEditDuplicate}
        title="Edit the new duplicate?"
        size="sm"
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
              <RiCheckLine className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Duplicate created{duplicatedQuote?.quote_number ? (
                <> — <span className="font-semibold">{duplicatedQuote.quote_number}</span></>
              ) : null}.{' '}
              Do you want to edit it now?
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={dismissEditDuplicate}
              className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100"
            >
              Not now
            </button>
            <button
              type="button"
              onClick={acceptEditDuplicate}
              className="px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800 inline-flex items-center gap-2"
            >
              <RiPencilLine className="w-4 h-4" />
              Edit now
            </button>
          </div>
        </div>
      </Modal>
    </Drawer>
  );
};

export default QuoteDetailsDrawer;